import { useState, useEffect, useRef } from 'react'
import { Upload, Trash2, FileCode, Package, Clock, HardDrive, AlertCircle } from 'lucide-react'
import { Button, Card, Badge } from '.'
import { sourceMapsApi } from '@/api'
import type { SourceMap } from '@/api/sourceMaps'
import toast from 'react-hot-toast'

interface SourceMapsListProps {
  appId: string
}

function formatFileSize(bytes: number | null): string {
  if (bytes === null) return 'Unknown'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function SourceMapsList({ appId }: SourceMapsListProps) {
  const [sourceMaps, setSourceMaps] = useState<SourceMap[]>([])
  const [releases, setReleases] = useState<string[]>([])
  const [selectedRelease, setSelectedRelease] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadRelease, setUploadRelease] = useState('')
  const [showUploadModal, setShowUploadModal] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const loadSourceMaps = async () => {
    try {
      const [mapsResponse, releasesResponse] = await Promise.all([
        sourceMapsApi.list(appId, selectedRelease || undefined),
        sourceMapsApi.listReleases(appId),
      ])
      setSourceMaps(mapsResponse.source_maps)
      setReleases(releasesResponse)
    } catch (error) {
      console.error('Failed to load source maps:', error)
      toast.error('Failed to load source maps')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadSourceMaps()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, selectedRelease])

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    if (!uploadRelease.trim()) {
      toast.error('Please enter a release version')
      return
    }

    setIsUploading(true)
    setUploadProgress(0)

    const totalFiles = files.length
    let uploaded = 0

    try {
      for (const file of Array.from(files)) {
        if (!file.name.endsWith('.map')) {
          toast.error(`Skipping ${file.name} - not a .map file`)
          continue
        }

        await sourceMapsApi.upload(appId, file, uploadRelease.trim(), percent => {
          // Calculate overall progress across all files
          const fileProgress = percent / totalFiles
          const previousFilesProgress = (uploaded / totalFiles) * 100
          setUploadProgress(Math.round(previousFilesProgress + fileProgress))
        })

        uploaded++
      }

      toast.success(`Uploaded ${uploaded} source map${uploaded !== 1 ? 's' : ''}`)
      setShowUploadModal(false)
      setUploadRelease('')
      loadSourceMaps()
    } catch (error) {
      console.error('Upload failed:', error)
      toast.error('Failed to upload source map')
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
    }
  }

  const handleDelete = async (sourceMap: SourceMap) => {
    if (!confirm(`Delete source map "${sourceMap.filename}" for release "${sourceMap.release}"?`)) {
      return
    }

    try {
      await sourceMapsApi.delete(appId, sourceMap.id)
      setSourceMaps(sourceMaps.filter(sm => sm.id !== sourceMap.id))
      toast.success('Source map deleted')
    } catch {
      toast.error('Failed to delete source map')
    }
  }

  const handleDeleteRelease = async (release: string) => {
    const count = sourceMaps.filter(sm => sm.release === release).length
    if (!confirm(`Delete all ${count} source maps for release "${release}"?`)) {
      return
    }

    try {
      await sourceMapsApi.deleteByRelease(appId, release)
      setSourceMaps(sourceMaps.filter(sm => sm.release !== release))
      setReleases(releases.filter(r => r !== release))
      toast.success(`Deleted ${count} source maps for release ${release}`)
    } catch {
      toast.error('Failed to delete source maps')
    }
  }

  // Group source maps by release
  const groupedByRelease = sourceMaps.reduce(
    (acc, sm) => {
      if (!acc[sm.release]) {
        acc[sm.release] = []
      }
      acc[sm.release].push(sm)
      return acc
    },
    {} as Record<string, SourceMap[]>
  )

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-slate-200 rounded animate-pulse w-48" />
        <div className="h-24 bg-slate-200 rounded animate-pulse" />
        <div className="h-24 bg-slate-200 rounded animate-pulse" />
      </div>
    )
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Source Maps</h3>
            <p className="text-sm text-slate-500">
              Upload source maps to view original source code in stack traces
            </p>
          </div>
          <Button onClick={() => setShowUploadModal(true)} className="flex items-center gap-2">
            <Upload className="w-4 h-4" />
            Upload Source Maps
          </Button>
        </div>

        {releases.length > 1 && (
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-600">Filter by release:</label>
            <select
              value={selectedRelease}
              onChange={e => setSelectedRelease(e.target.value)}
              className="text-sm border border-slate-200 rounded px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-accent-500"
            >
              <option value="">All releases</option>
              {releases.map(release => (
                <option key={release} value={release}>
                  {release}
                </option>
              ))}
            </select>
          </div>
        )}

        {sourceMaps.length === 0 ? (
          <Card className="p-8 text-center">
            <FileCode className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-slate-900 mb-2">No source maps uploaded</h4>
            <p className="text-slate-500 mb-4 max-w-md mx-auto">
              Upload source maps to see original file names and line numbers in JavaScript stack
              traces. This helps debug minified production code.
            </p>
            <Button
              onClick={() => setShowUploadModal(true)}
              className="inline-flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              Upload Your First Source Map
            </Button>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedByRelease)
              .sort(([a], [b]) => b.localeCompare(a)) // Sort releases descending
              .map(([release, maps]) => (
                <Card key={release} className="overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-slate-500" />
                      <span className="font-medium text-slate-900">Release: {release}</span>
                      <Badge variant="gray" className="text-xs">
                        {maps.length} file{maps.length !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                    <button
                      onClick={() => handleDeleteRelease(release)}
                      className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete all
                    </button>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {maps.map(sourceMap => (
                      <div
                        key={sourceMap.id}
                        className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <FileCode className="w-5 h-5 text-slate-400" />
                          <div>
                            <p className="font-medium text-slate-900">{sourceMap.filename}</p>
                            {sourceMap.original_filename && (
                              <p className="text-xs text-slate-500">
                                Maps to: {sourceMap.original_filename}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-1 text-xs text-slate-500">
                            <HardDrive className="w-3.5 h-3.5" />
                            {formatFileSize(sourceMap.file_size_bytes)}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-slate-500">
                            <Clock className="w-3.5 h-3.5" />
                            {formatDate(sourceMap.created_at)}
                          </div>
                          <button
                            onClick={() => handleDelete(sourceMap)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Delete source map"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
          </div>
        )}

        {/* Help text */}
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">How to use source maps</p>
              <ol className="list-decimal ml-4 space-y-1 text-blue-700">
                <li>Generate source maps during your build (e.g., Webpack, Vite, esbuild)</li>
                <li>
                  Upload .map files using our{' '}
                  <a
                    href="https://github.com/roselabs-io/falcon-upload-sourcemaps"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-blue-900"
                  >
                    GitHub Action
                  </a>{' '}
                  or manually here
                </li>
                <li>
                  Set the same release version in your Falcon SDK:{' '}
                  <code className="bg-blue-100 px-1 rounded">release: &apos;1.2.0&apos;</code>
                </li>
                <li>JavaScript errors will now show original file names and line numbers</li>
              </ol>
            </div>
          </div>
        </Card>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Upload Source Maps</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Release Version
                </label>
                <input
                  type="text"
                  value={uploadRelease}
                  onChange={e => setUploadRelease(e.target.value)}
                  placeholder="e.g., 1.2.0"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent-500"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Must match the release version set in your Falcon SDK
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Source Map Files
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".map,application/json"
                  multiple
                  onChange={e => {
                    if (!isUploading && e.target.files) {
                      handleUpload(e.target.files)
                    }
                  }}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading || !uploadRelease.trim()}
                  className="w-full border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-accent-500 hover:bg-accent-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploading ? (
                    <div className="space-y-2">
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div
                          className="bg-accent-500 h-2 rounded-full transition-all"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                      <p className="text-sm text-slate-600">Uploading... {uploadProgress}%</p>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-600">
                        Click to select .map files or drag and drop
                      </p>
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowUploadModal(false)
                  setUploadRelease('')
                }}
                disabled={isUploading}
              >
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  )
}
